<script setup lang="ts">
import { defineEmits, defineProps, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import BaseModal from "@/views/Auth/components/BaseModal.vue";
import LoaderIcon from "@/components/icons/LoaderIcon.vue";
import { fetchStripeUrl } from "@/api/subscription";
import AppDropdown from "@/components/inputs/AppDropdown.vue";
import ChevronIcon from "@/components/icons/ChevronIcon.vue";
import { SubscriptionPrice, Subscription } from "@/types/subscription";
import { useSubscriptionPricesStore } from "@/stores/subscriptionPricesStore";
import { PREMIUM_CALCULATORS, PremiumCalculator } from "@/types/premiumTypes";
import activationCalendarIcon from "@/assets/icons/menu/activationCalendar.svg";
import layoutRoomsIcon from "@/assets/icons/menu/layoutRooms.svg";
import destinyMatrixIcon from "@/assets/icons/menu/destinyMatrix.svg";

const props = defineProps<{
  show: boolean;
  defaultSelected: PremiumCalculator;
  currentSubscriptions: Subscription[];
}>();
const emits = defineEmits(["close"]);

const { t } = useI18n();
const { getSubscriptionPrices } = useSubscriptionPricesStore();
const waitApiCall = ref(false);
const isDropdownOpen = ref(false);
const subscriptionPrices = ref<SubscriptionPrice[]>([]);
const selectedPrice = ref<SubscriptionPrice>();
const selectedCalculator = ref<PremiumCalculator>();

const currencies = {
  uah: "₴",
  usd: "$",
};
const ALL_PREMIUM_CALCULATORS = Object.values(PREMIUM_CALCULATORS);

const calculatorsWithTips: PremiumCalculator[] = [
  PREMIUM_CALCULATORS.activations,
];

onMounted(async () => {
  await handleChangeCalculator(props.defaultSelected);
});

async function handleSubscribe() {
  if (!selectedPrice.value) {
    return;
  }
  try {
    waitApiCall.value = true;
    const stripeUrl = await fetchStripeUrl(selectedPrice.value);
    if (stripeUrl) {
      window.location.href = stripeUrl;
    }
  } catch (e) {
    console.error(e);
  } finally {
    waitApiCall.value = false;
  }
}

function selectItem(item: SubscriptionPrice) {
  selectedPrice.value = item;
  isDropdownOpen.value = false;
}

function getCalculatorIcon(premiumCalculator: PremiumCalculator) {
  switch (premiumCalculator) {
    case PREMIUM_CALCULATORS.activations:
      return activationCalendarIcon;
    case PREMIUM_CALCULATORS.apartmentPlan:
      return layoutRoomsIcon;
    case PREMIUM_CALCULATORS.destinyMatrix:
      return destinyMatrixIcon;
    default:
      return null;
  }
}

function isActiveSubscription(premiumCalculator: PremiumCalculator) {
  if (props.currentSubscriptions === undefined) {
    return false;
  }
  const subscription = props.currentSubscriptions.find(
    (sub) => sub.is_active && sub.calculator === premiumCalculator
  );

  return subscription?.is_active ?? false;
}

async function handleChangeCalculator(premiumCalculator: PremiumCalculator) {
  try {
    waitApiCall.value = true;
    subscriptionPrices.value = await getSubscriptionPrices(premiumCalculator);
    selectedCalculator.value = premiumCalculator;
    selectedPrice.value = subscriptionPrices.value[0];
  } catch (e) {
    console.error(e);
  } finally {
    waitApiCall.value = false;
  }
}
</script>

<template>
  <BaseModal :show="show" @close="emits('close')" max-height="472px">
    <p class="text-2xl font-semibold text-black mb-6 select-none">
      {{ t("subscription.premium-title") }}
    </p>
    <div class="flex max-md:flex-col flex-wrap gap-4 mb-6 md:mb-10">
      <div
        v-for="premiumCalculator in ALL_PREMIUM_CALCULATORS"
        :key="premiumCalculator"
        class="w-full h-[52px] md:w-[170px] md:h-[136px] border p-2 md:p-4 rounded-lg flex gap-2 md:flex-col md:gap-4 max-md:items-center relative select-none"
        :class="{
          'border-active bg-hover': selectedCalculator === premiumCalculator,
          'border-muted': selectedCalculator !== premiumCalculator,
          'cursor-pointer':
            !isActiveSubscription(premiumCalculator) &&
            selectedCalculator !== premiumCalculator,
        }"
        @click="
          !isActiveSubscription(premiumCalculator)
            ? handleChangeCalculator(premiumCalculator)
            : null
        "
      >
        <!-- CALCULATOR ICON -->
        <div
          class="size-9 md:size-12 bg-white rounded-full flex items-center justify-center"
        >
          <img
            :src="getCalculatorIcon(premiumCalculator)"
            alt=""
            class="size-6 md:size-8"
          />
        </div>
        <!-- CALCULATOR NAME -->
        <p class="text-brown text-sm">
          {{ t(`subscription.${premiumCalculator}.name`) }}
        </p>
        <!-- IS ACTIVE BUTTON -->
        <div
          v-if="isActiveSubscription(premiumCalculator)"
          class="w-[65px] h-6 bg-active rounded-lg px-2 py-1 text-white text-xs sf-pro absolute top-[14px] right-2 md:top-1 md:right-1"
        >
          Активна
        </div>
      </div>
    </div>
    <!-- PRICES DROPDOWN -->
    <AppDropdown
      v-if="subscriptionPrices.length > 0"
      :label="t('subscription.premium-type')"
      :items="subscriptionPrices"
      :selectedItem="selectedPrice"
      :isDropdownOpen="isDropdownOpen"
      :class="
        calculatorsWithTips.includes(selectedCalculator) ? 'mb-2' : 'mb-6'
      "
      @toggle="isDropdownOpen = !isDropdownOpen"
    >
      <template #selectedItem="{ item }">
        <span
          class="flex justify-between items-center px-4 text-base text-brown"
        >
          <span>
            {{ t(`subscription.${selectedCalculator}.tiers.${item.tier}`) }}
          </span>
          <span class="flex gap-4">
            <span>
              {{ currencies[item.currency] }}{{ item.amount }}/{{
                t(`subscription.interval.${item.interval}`)
              }}
            </span>
            <ChevronIcon
              class="transition-transform"
              :class="isDropdownOpen ? 'rotate-180' : 'rotate-0'"
            />
          </span>
        </span>
      </template>
      <template #dropdownItem="{ item }">
        <div
          class="flex items-center px-4 text-base text-brown h-10 hover:bg-divider cursor-pointer select-none"
          @click="() => selectItem(item)"
        >
          <div class="flex-1">
            {{ t(`subscription.${selectedCalculator}.tiers.${item.tier}`) }}
          </div>
          <div class="flex-1 flex justify-end">
            {{ currencies[item.currency] }}{{ item.amount }}/{{
              t(`subscription.interval.${item.interval}`)
            }}
          </div>
        </div>
      </template>
    </AppDropdown>
    <p
      v-if="
        selectedPrice && calculatorsWithTips.includes(selectedPrice.calculator)
      "
      class="text-center text-brown text-sm mb-6"
    >
      {{
        t(`subscription.${selectedPrice.calculator}.tips.${selectedPrice.tier}`)
      }}
    </p>
    <!-- CONFIRM BUTTON -->
    <button
      v-if="selectedPrice"
      class="w-full h-14 rounded-full bg-orange-gradient"
      :class="{
        'opacity-40 pointer-events-none': waitApiCall,
      }"
      @click="handleSubscribe"
    >
      <LoaderIcon v-if="waitApiCall" />
      <span
        v-else
        class="font-semibold text-white text-xl leading-[24px] select-none"
      >
        {{
          t("buttons.subscription.confirm-subscription", {
            currency: currencies[selectedPrice.currency],
            price: selectedPrice.amount,
          })
        }}
      </span>
    </button>
  </BaseModal>
</template>
